<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Rechnungsnummern') }}
            <img
                src="@img/info.png"
                alt=""
                :title="$t('Clubity generiert nach dem unten definierten Schema automatisch Rechnungsnummern für Rechnungen.')"
            />
        </h3>

        <div>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <div class="animated fadeIn">
                            <kgr-form-row
                                :label="$t('Kürzel')"
                                :error="error"
                                field-name="invoiceNumberConfig[prefix]"
                            >
                                <input
                                    v-model.trim="invoiceNumberConfigData.prefix"
                                    type="text"
                                    class="kgr-input"
                                    maxlength="20"
                                    @keyup.enter="onKeyUpEnter"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="$t('Rechnungsnummern von')"
                                :tooltip="$t('Diese Einstellung kann nur vorgenommen werden, solange es keine erstellten Rechnungen gibt.')"
                                :error="error"
                                field-name="invoiceNumberConfig[initialNumber]"
                                required
                            >
                                <input
                                    v-model.number="invoiceNumberConfigData.initialNumber"
                                    type="number"
                                    min="1"
                                    max="999999999"
                                    class="kgr-input"
                                    :disabled="hasInvoices"
                                    @keyup.enter="onKeyUpEnter"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="invoiceNumberConfig[useYear]"
                            >
                                <kgr-checkbox
                                    v-model="invoiceNumberConfigData.useYear"
                                    :label="$t('Jahr in die Rechnungsnummer übernehmen')"
                                    class="kgr-row"
                                    @keyup.enter="onKeyUpEnter"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="invoiceNumberConfig[useMonth]"
                            >
                                <kgr-checkbox
                                    v-model="invoiceNumberConfigData.useMonth"
                                    :label="$t('Monat in die Rechnungsnummer übernehmen')"
                                    class="kgr-row"
                                    @keyup.enter="onKeyUpEnter"
                                />
                            </kgr-form-row>

                            <div
                                v-if="invoiceNumberConfigData.useYear||invoiceNumberConfigData.useMonth"
                                style="margin-left:40px"
                            >
                                <kgr-form-row
                                    :error="error"
                                    field-name="invoiceNumberConfig[resetNumberYearly]"
                                >
                                    <kgr-checkbox
                                        v-model="invoiceNumberConfigData.resetNumberYearly"
                                        :label="$t('Rechnungsnummer zu Beginn eines neuen Kalenderjahres auf den Ursprungswert zurücksetzen.')"
                                        class="kgr-row"
                                        @keyup.enter="onKeyUpEnter"
                                    />
                                </kgr-form-row>
                            </div>

                            <kgr-form-row
                                :error="error"
                                field-name="invoiceNumberConfig[padNumberWithLeadingZeros]"
                            >
                                <kgr-checkbox
                                    v-model="invoiceNumberConfigData.padNumberWithLeadingZeros"
                                    :label="$t('Führende Nullen verwenden')"
                                    class="kgr-row"
                                    @change="onUpdatedPadNumberWithLeadingZeros()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                v-if="invoiceNumberConfig.padNumberWithLeadingZeros"
                                :label="$t('Stellen der Rechnungsnummern')"
                                :error="error"
                                field-name="invoiceNumberConfig[numberDigits]"
                                class="animated fadeIn"
                            >
                                <kgr-generic-select
                                    v-model="invoiceNumberConfigData.numberDigits"
                                    :options="filteredInvoiceNumberDigits"
                                    no-null-option
                                    @input="onKeyUpEnter"
                                />
                            </kgr-form-row>
                        </div>
                    </div>
                    <div class="animated fadeIn kgr-grid__cell">
                        <div class="kgr-region kgr-region--rounded kgr-region--gray">
                            <div class="kgr-row">
                                {{ $t('So sieht deine erste Rechnungsnummer aus') }}:
                            </div>
                            <div class="invoiceSettingsPreview kgr-text kgr-text--bold">
                                {{ invoiceNumberPreview }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '../../common/form/checkbox/Checkbox';
import Error from '../../../js/model/Error';
import FormRow from '../../common/form/FormRow';
import GenericSelect from '../../common/form/select/GenericSelect';
import InvoiceCount from '../../../js/dto/InvoiceCount';
import InvoiceNumberConfig from '../../../js/dto/InvoiceNumberConfig';

export default {
    components: {
        'kgr-generic-select': GenericSelect,
        'kgr-checkbox': Checkbox,
        'kgr-form-row': FormRow,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        invoiceNumberConfig: {
            type: InvoiceNumberConfig,
            required: true,
        },
        invoiceCount: {
            type: InvoiceCount,
            required: true,
        },
        biggestInvoiceNumberLength: {
            type: Number,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
    },
    data() {
        return {
            invoiceNumberConfigData: this.invoiceNumberConfig,
        };
    },
    watch: {
        invoiceNumberConfig(newConfig) {
            this.invoiceNumberConfigData = newConfig;
        },
    },
    computed: {
        invoiceNumberDigits() {
            return [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        },
        filteredInvoiceNumberDigits() {
            return this.invoiceNumberDigits.filter(element => element >= this.biggestInvoiceNumberLength);
        },
        invoiceNumberPreview() {
            let separator = '-';
            let prefix = '';

            if (this.invoiceNumberConfig.prefix) {
                prefix = this.invoiceNumberConfig.prefix + separator;
            }
            let year = '';
            if (this.invoiceNumberConfig.useYear) {
                year = this.getYear() + separator;
            }
            let month = '';
            if (this.invoiceNumberConfig.useMonth) {
                month = this.getMonth() + separator;
            }
            let number = this.invoiceNumberConfig.initialNumber;
            if (this.invoiceNumberConfig.padNumberWithLeadingZeros) {
                number = this.pad(number, this.invoiceNumberConfig.numberDigits);
            }
            return prefix + year + month + number;
        },
        hasInvoices() {
            return this.invoiceCount.count > 0;
        },
    },
    methods: {
        onKeyUpEnter() {
            this.error.clear('invoiceNumberConfig[numberDigits]');
            if (this.invoiceNumberConfigData.numberDigits < this.biggestInvoiceNumberLength) {
                this.error.add('invoiceNumberConfig[numberDigits]', this.$t('Die Anzahl der Stellen kann nicht kleiner gestellt werden, als bereits vorhandene Rechnungsnummern Stellen haben ({counter}).', { 'counter': this.biggestInvoiceNumberLength }));
                return;
            }

            this.$emit('enter', this.invoiceNumberConfigData);
        },
        onUpdatedPadNumberWithLeadingZeros() {
            if (this.invoiceNumberConfig.padNumberWithLeadingZeros && null === this.invoiceNumberConfig.numberDigits) {
                this.invoiceNumberConfigData.numberDigits = this.filteredInvoiceNumberDigits[0];
            }
        },
        pad(str, max) {
            str = str.toString();
            return str.length < max ? this.pad('0' + str, max) : str;
        },
        getYear() {
            let today = new Date();
            return today.getFullYear();
        },
        getMonth() {
            let today = new Date();
            return ('0' + (today.getMonth()+1)).slice(-2);
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
